import { Env } from '@env';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useLocalSearchParams, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Modal from 'react-native-modal';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type {
  ChargingRecord,
  Incidental,
  ReservationAdjustment,
  Toll,
} from '@/api';
import { useCustomer } from '@/api/customers/use-customer';
import { useDeleteToll } from '@/api/partners/reservations/use-delete-toll';
import { useDeleteTollImage } from '@/api/partners/reservations/use-delete-toll-image';
import { useReservation } from '@/api/partners/reservations/use-reservation';
import { useUpdateReservation } from '@/api/partners/reservations/use-update-reservation';
import { useUploadTripPhotos } from '@/api/partners/reservations/use-upload-trip-photos';
import { useSubscriptionEstimatedEarnings } from '@/api/partners/subscriptions/use-estimated-earnings';
import {
  capitalizeFirstLetter,
  compressAndResize,
  formatNumber,
  isDesktop,
  runWithConcurrencyLimit,
  snakeToTitleCase,
  uniqByFilename,
  useAuth,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import Accordion from '@/ui/accordion';
import Alert, { AlertType } from '@/ui/alert';
import { Chip, ChipType } from '@/ui/chip';
import DataTable from '@/ui/data-table';
import { FileThumbnail } from '@/ui/file-thumbnail';
import { Icon } from '@/ui/icon';
import { ArrowRight, Home } from '@/ui/icons';
import { ArrowLeft } from '@/ui/icons/arrow-left';
import { Cancel } from '@/ui/icons/cancel';
import { Dot } from '@/ui/icons/dot';
import { Location } from '@/ui/icons/location';
import { TrashFill } from '@/ui/icons/trash-fill';
import { InputMulti } from '@/ui/input-multi';
import { Loader } from '@/ui/loader';
import PartnerFileItem from '@/ui/partner-file-item';
import { Tooltip } from '@/ui/tooltip';

import { KeyLimitModal } from '../cars/key-limit-modal';
import { SetupDigitalKeyModal } from '../cars/setup-digital-key-modal';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';
import {
  CAR_ALERT_CONTENT,
  CarAlertType,
  getReservationCarAlerts,
} from '../reservations/car-alerts';
import {
  getPartnerReservationStatusColor,
  getPartnerReservationStatusLabel,
} from '../reservations/status-label';
import SuperchargingCard from '../supercharging-card';
import TollCard from '../toll-card';
import { AddChargingRecordModal } from './add-charging-record-modal';
import { AddIncidentalModal } from './add-incidental-modal';
import { AddTollModal } from './add-toll-modal';
import ModifyRentalModal from './modify-rental-modal';
import { PendingActions } from './pending-actions';

function getIncidentalStatusChipType(status: string): ChipType {
  switch (status) {
    case 'approved':
    case 'issued':
      return ChipType.SUCCESS;
    case 'denied':
    case 'rejected':
      return ChipType.ERROR;
    case 'pending':
      return ChipType.PENDING;
    default:
      return ChipType.BRAND;
  }
}

export default function EditReservation() {
  const { theme, styles } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const status = useAuth.use.status();
  const router = useRouter();
  const { showActionSheetWithOptions } = useActionSheet();
  const [showModifyRentalModal, setShowModifyRentalModal] = useState(false);
  const [showAddTollModal, setShowAddTollModal] = useState(false);
  const [showAddChargingRecordModal, setShowAddChargingRecordModal] =
    useState(false);
  const [showAddIncidentalModal, setShowAddIncidentalModal] = useState(false);
  const [showSetupDigitalKeyModal, setShowSetupDigitalKeyModal] =
    useState(false);
  const [showKeyLimitModal, setShowKeyLimitModal] = useState(false);
  const [startingMiles, setStartingMiles] = useState('0');
  const [endingMiles, setEndingMiles] = useState('0');
  const [notes, setNotes] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [images, setImages] = useState<ImagePicker.ImagePickerAsset[]>();
  const [showGallery, setShowGallery] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState<string[]>([]);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const progress = useSharedValue<number>(0);
  const carouselRef = useRef<ICarouselInstance>(null);

  const { data, refetch, isLoading } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  const { data: customer } = useCustomer({
    enabled: false,
  });

  const { refetch: refetchSubscriptionEstimatedEarnings } =
    useSubscriptionEstimatedEarnings({
      //@ts-ignore
      variables: { reservationId: local.id },
      enabled: !!local.id && !!data?.subscription,
    });

  const { mutateAsync: updateReservation, isPending: isUpdatingReservation } =
    useUpdateReservation();

  const { mutateAsync: uploadTripPhotos, isPending: isUploadingTripPhotos } =
    useUploadTripPhotos();

  const { mutateAsync: deleteToll } = useDeleteToll();

  const { mutateAsync: deleteTollImage } = useDeleteTollImage();

  const [deletingTollId, setDeletingTollId] = useState<number | null>(null);
  const [deletingTollImageUrl, setDeletingTollImageUrl] = useState<
    string | null
  >(null);

  const { isEVFMC } = useHasOrganization();

  const isResla = useMemo(
    () => customer?.email === 'teslarents@equipmentshare.com',
    [customer]
  );

  const carAlerts = useMemo(
    () => getReservationCarAlerts(data?.status, data?.car),
    [data?.status, data?.car]
  );

  const handleReconnectCar = async () => {
    const redirectUri = `${Env.STANDARD_FLEET_REDIRECT_URI}&reservationId=${data.id}`;
    const baseUrl = `${
      Env.STANDARD_FLEET_URI
    }&redirect_uri=${encodeURIComponent(redirectUri)}&partner_ref=${
      customer?.partnerToken
    }`;

    if (Platform.OS === 'web') {
      window.open(baseUrl, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(baseUrl);
    }
  };

  const handleCarAlertPress = (alert: CarAlertType) => {
    if (alert === CarAlertType.CAR_DISCONNECTED) {
      handleReconnectCar();
    } else if (alert === CarAlertType.DIGITAL_KEY_DISCONNECTED) {
      setShowSetupDigitalKeyModal(true);
    } else if (alert === CarAlertType.KEY_LIMIT) {
      setShowKeyLimitModal(true);
    }
  };

  useEffect(() => {
    if (status === 'signIn') {
      refetch();

      if (data?.subscription) {
        refetchSubscriptionEstimatedEarnings();
      }
    }
  }, [status]);

  useEffect(() => {
    if (data) {
      setStartingMiles(data.startingMileage?.toString() || '0');
      setEndingMiles(data.endingMileage?.toString() || '0');
      setNotes(data.hostNotes || '');
    }
  }, [data]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Edit Reservation',
    });
  }, []);

  const imgsToShow = useMemo(() => {
    if (!data) return [];

    // return data.preTripCarImages : data.postTripCarImages;

    return data.carImages;
  }, [data]);

  useEffect(() => {
    const convertToFiles = async () => {
      const convertedImages = await Promise.all(
        imgsToShow.map(async (carImage) => {
          const response = await fetch(carImage.photo);
          const blob = await response.blob();
          const fileName = carImage.photo.split('/').pop();

          const file = new File([blob], fileName!, {
            type: blob.type,
          });

          return {
            file,
            name: carImage.photo.split('/').pop(),
            uri: carImage.photo, // Add the uri property for native platforms
            createdAt: carImage.createdAt,
            uploadedBy:
              carImage.userName ||
              `${customer?.firstname} ${customer?.lastname}`,
          };
        })
      );

      setImages(convertedImages);
    };

    if (data?.carImages.length) {
      convertToFiles();
    }
  }, [data?.carImages]);

  const allowUploadingCarImages = useMemo(() => {
    if (!data) return false;

    const now = DateTime.now();
    const startTime = DateTime.fromISO(data.startTime);
    const endTime = DateTime.fromISO(data.endTime);

    const validBeforeTrip =
      now > startTime.minus({ hours: 24 }) &&
      now < startTime.plus({ hours: 6 });
    const validAfterTrip =
      now > endTime.minus({ hours: 1 }) && now < endTime.plus({ hours: 24 });

    return validBeforeTrip || validAfterTrip;
  }, [data]);

  const allowUploadingChargingRecords = useMemo(() => {
    if (!data) return false;

    const now = DateTime.now();
    const endTime = DateTime.fromISO(data.endTime);

    return now > endTime && now < endTime.plus({ hours: 72 });
  }, [data]);

  const tollOffsetCutoffTimeInDays = useMemo(() => {
    return isResla ? 7 : 3;
  }, [isResla]);

  const allowUploadingTolls = useMemo(() => {
    if (!data) return false;

    const now = DateTime.now();

    const cutoffTime = DateTime.fromISO(data.endTime).plus({
      days: tollOffsetCutoffTimeInDays,
    });

    return now < cutoffTime && now > DateTime.fromISO(data.endTime);
  }, [data, tollOffsetCutoffTimeInDays]);

  const allowUploadingIncidental = useMemo(() => {
    if (!data) return false;

    const now = DateTime.now();

    const threeMonthsAfterTripEnds = DateTime.fromISO(data.endTime).plus({
      months: 3,
    });

    return (
      now < threeMonthsAfterTripEnds && now > DateTime.fromISO(data.startTime)
    );
  }, [data]);

  const handleDeleteToll = async (tollId: number) => {
    try {
      setDeletingTollId(tollId);
      await deleteToll({ id: data.id, tollId });
      await refetch();
      Toast.show({
        type: 'success',
        text1: 'Toll deleted',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    } finally {
      setDeletingTollId(null);
    }
  };

  const handleDeleteTollImage = async (imageUrl: string) => {
    try {
      setDeletingTollImageUrl(imageUrl);
      const imagePath = imageUrl.replace(/^https?:\/\/[^/]+/, '');
      await deleteTollImage({ id: data.id, imageUrl: imagePath });
      await refetch();
      Toast.show({
        type: 'success',
        text1: 'Toll image deleted',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    } finally {
      setDeletingTollImageUrl(null);
    }
  };

  const tollRows = useMemo(() => {
    if (!data) return [];

    return data.tolls.map((toll: Toll) => {
      return {
        key: `toll-${toll.id}`,
        id: `toll-${toll.id}`,
        'toll-plaza-name': <Text type="bodyRegular">{toll.name}</Text>,
        time: (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {format(
              toZonedTime(toll.startTimestamp, data.pickupLocation.timezone),
              'MMM dd yyyy - hh:mm a'
            )}
          </Text>
        ),
        amount: (
          <Text type="bodyRegular">{`$${formatNumber(toll.adminAmount)}`}</Text>
        ),
        ...(isResla
          ? {
              actions:
                deletingTollId === toll.id ? (
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ActivityIndicator
                      size="small"
                      color={theme.colors.icon.color}
                    />
                  </View>
                ) : (
                  <Pressable
                    onPress={() => handleDeleteToll(toll.id)}
                    disabled={deletingTollId !== null}
                    style={({ pressed }) => ({
                      width: 32,
                      height: 32,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: pressed || deletingTollId !== null ? 0.5 : 1,
                      borderRadius: 100,
                    })}
                  >
                    <Icon
                      type="naked"
                      icon={<TrashFill />}
                      fillColor={theme.colors.icon.color}
                      width={14}
                      height={14}
                    />
                  </Pressable>
                ),
            }
          : {}),
      };
    });
  }, [data, isResla, deletingTollId]);

  const tollFooterValues = useMemo(() => {
    if (!data) return null;

    const amountTotal = data.tolls.reduce(
      (total, toll) => total + parseFloat(toll.adminAmount),
      0
    );

    return {
      'toll-plaza-name': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      amount: (
        <Text
          type="caption1"
          style={{ color: theme.colors.text.secondary }}
        >{`$${formatNumber(amountTotal)}`}</Text>
      ),
    };
  }, [data]);

  const chargingRecordRows = useMemo(() => {
    if (!data) return [];

    return data.chargingRecords.map((record: ChargingRecord) => {
      return {
        key: `charging-${record.id}`,
        id: `charging-${record.id}`,
        'charging-plaza-name': <Text type="bodyRegular">{record.name}</Text>,
        time: (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {format(
              toZonedTime(record.startTimestamp, data.pickupLocation.timezone),
              'MMM dd yyyy - hh:mm a'
            )}
          </Text>
        ),
        amount: (
          <Text type="bodyRegular">{`$${formatNumber(record.amount)}`}</Text>
        ),
      };
    });
  }, [data]);

  const chargingRecordFooterValues = useMemo(() => {
    if (!data) return null;

    const amountTotal = data.chargingRecords.reduce(
      (total, record) => total + parseFloat(record.amount),
      0
    );

    return {
      'charging-plaza-name': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      amount: (
        <Text
          type="caption1"
          style={{ color: theme.colors.text.secondary }}
        >{`$${formatNumber(amountTotal)}`}</Text>
      ),
    };
  }, [data, theme.colors.text.secondary]);

  const incidentalRecordRows = useMemo(() => {
    if (!data) return [];

    return data.incidentals.map((record: Incidental) => {
      const paymentStatus = record.isPaid ? (
        <View>
          <Text type="caption1">
            {record.isPaidAt
              ? `Paid ${format(
                  toZonedTime(record.isPaidAt, data.pickupLocation.timezone),
                  'MMMM d, yyyy'
                )}`
              : 'Paid'}
          </Text>
        </View>
      ) : record.claimOwnerResponse ? (
        <View>
          <Text type="caption1">{record.claimOwnerResponse}</Text>
        </View>
      ) : null;

      return {
        key: `incidental-${record.id}`,
        id: <Text type="bodyRegular">{record.id}</Text>,
        name: (
          <Text type="bodyRegular" style={{ color: theme.colors.text.primary }}>
            {snakeToTitleCase(record.category)}
          </Text>
        ),
        time: (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {format(
              toZonedTime(record.offenseDate, data.pickupLocation.timezone),
              'MMM dd yyyy - hh:mm a'
            )}
          </Text>
        ),
        amount: (
          <Text type="bodyRegular">{`$${formatNumber(record.amount)}`}</Text>
        ),
        status: (
          <Chip
            type={getIncidentalStatusChipType(record.status)}
            icon={<Dot />}
            text={capitalizeFirstLetter(record.status)}
            iconWidth={8}
            iconHeight={8}
            style={{ alignSelf: 'center' }}
          />
        ),
        'payment-status': paymentStatus,
      };
    });
  }, [data]);

  const incidentalFooterValues = useMemo(() => {
    if (!data) return null;

    const amountTotal = data.incidentals.reduce(
      (total, record) => total + record.amount,
      0
    );

    return {
      id: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      amount: (
        <Text
          type="caption1"
          style={{ color: theme.colors.text.secondary }}
        >{`$${formatNumber(amountTotal)}`}</Text>
      ),
    };
  }, [data]);

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: true,
    });

    setActivePhotoIdx(index);
  };

  const handleUpdateTrip = async () => {
    try {
      await updateReservation({
        id: data.id,
        startingMileage: startingMiles,
        endingMileage: endingMiles,
        notes,
      });

      refetch();

      Toast.show({
        text1: 'Reservation updated',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Error updating:', error);
    }
  };

  const handleUploadPhotos = async (replaceImageFileName = null) => {
    let imgs = replaceImageFileName
      ? images?.filter(
          (img) => (img.fileName || img.name) !== replaceImageFileName
        )
      : images;

    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: ['image/jpeg', 'image/png', 'image/heic'],
          copyToCacheDirectory: false,
          multiple: true,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map(async (asset) => {
              const response = await fetch(asset.uri);
              const blob = await response.blob();

              const file = new File([blob], asset.name || 'file.jpg', {
                type: asset.mimeType || 'image/jpeg',
              });

              return {
                file,
                name: asset.name,
                uri: asset.uri,
                uploadedBy: `${customer?.firstname} ${customer?.lastname}`,
              };
            })
          );

          setImages(uniqByFilename([...(imgs || []), ...assets]));
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          // case takeAPhotoIdx: {
          //   const permission = await ImagePicker.getCameraPermissionsAsync();
          //   if (!permission.granted) {
          //     const permissionResult =
          //       await ImagePicker.requestCameraPermissionsAsync();

          //     if (!permissionResult.granted) {
          //       return;
          //     }
          //   }

          //   const result = await ImagePicker.launchCameraAsync({
          //     base64: true,
          //   });

          //   if (!result.canceled) {
          //     setImages(uniqByFilename([...imgs, ...result.assets]));
          //   }

          //   break;
          // }
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: ['image/jpeg', 'image/png', 'image/heic'],
                copyToCacheDirectory: false,
                multiple: true,
              });

              if (!result.canceled) {
                const compressedResults = await runWithConcurrencyLimit(
                  result.assets.map((asset) => () => compressAndResize(asset)),
                  3 // concurrency limit
                );

                const formattedAssets = await Promise.all(
                  compressedResults.map(async (res, index) => {
                    const file: string = await FileSystem.readAsStringAsync(
                      res.uri,
                      {
                        encoding: FileSystem.EncodingType.Base64,
                      }
                    );

                    return {
                      ...result.assets[index],
                      fileName: result.assets[index].name,
                      base64: file,
                      uri: res.uri,
                      uploadedBy: `${customer?.firstname} ${customer?.lastname}`,
                    };
                  })
                );

                setImages(
                  uniqByFilename([...(imgs || []), ...formattedAssets])
                );
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: true,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              const compressedResults = await runWithConcurrencyLimit(
                result.assets.map((asset) => () => compressAndResize(asset)),
                3 // concurrency limit
              );

              const formattedResults = compressedResults.map((res, index) => ({
                ...result.assets[index],
                uri: res.uri,
                base64: res.base64,
                uploadedBy: `${customer?.firstname} ${customer?.lastname}`,
              }));

              setImages(uniqByFilename([...(imgs || []), ...formattedResults]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  const handleSavePhotos = async () => {
    try {
      const formattedImages =
        Platform.OS === 'web' ? images?.map((img) => img.file) : images;

      if (formattedImages!.length > 40) {
        showErrorMessage('You can only upload a maximum of 40 photos.');
        return;
      }

      await uploadTripPhotos({
        id: data.id,
        images: formattedImages,
      });

      refetch();

      Toast.show({
        type: 'success',
        text1: 'Photos uploaded',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  if (isLoading || isUploadingTripPhotos) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (!data)
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          paddingTop: 16,
        }}
      />
    );

  const isUnlimitedSubscription =
    !!data.subscription &&
    !!data.hostEarningsBreakdown &&
    'mileageTier' in data.hostEarningsBreakdown &&
    data.hostEarningsBreakdown.mileageTier === 'Unlimited';

  const isRegularSubscription = !!data.subscription && !isUnlimitedSubscription;

  const showCustomerPriceColumn = isEVFMC && !isRegularSubscription;

  const resolvedCustomerChargingAmount = data.passThroughCharging
    ? data.chargingUsed
    : data.chargingUsedWithUpcharge;

  const billedIncidentals = data.incidentals.filter(
    (incidental) => incidental.approvedForBilling
  );

  const showTotalEarnings =
    !isRegularSubscription ||
    ['cleaning', 'billable', 'completed'].includes(data.status);

  // Insurance is billed on the days actually charged (sum of reservation-rate
  // days), which for daily-cadence subs differs from the full trip span
  // (numDays). Falls back to numDays when there are no rates.
  const billedDays =
    data.reservationRates.reduce((sum, rate) => sum + rate.numDays, 0) ||
    data.numDays;

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        paddingTop: 16,
      }}
    >
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={() => {
              refetch();
            }}
          />
        }
      >
        <View style={{ flexGrow: 1, gap: 32 }}>
          <View style={styles.nav}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.navigate('/partner/reservations');
              }}
            />

            <Text type="title1">Rental ID: {data.id}</Text>
          </View>

          {data.status === 'pending' ? (
            <PendingActions reservationId={data.id} />
          ) : null}

          {carAlerts.length > 0 ? (
            <View style={{ gap: 16 }}>
              {carAlerts.map((alert) => (
                <Alert
                  key={alert}
                  type={AlertType.WARNING}
                  title={CAR_ALERT_CONTENT[alert].title}
                  body={CAR_ALERT_CONTENT[alert].body}
                  buttonText={CAR_ALERT_CONTENT[alert].buttonText}
                  onPress={() => handleCarAlertPress(alert)}
                />
              ))}
            </View>
          ) : null}

          <Card>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
                padding: 16,
              }}
            >
              <Text type="title2">
                {['open', 'verified', 'late', 'cancelled'].includes(data.status)
                  ? `${data.customerFirstname} ${
                      data.customerLastname?.[0] ?? ''
                    }.`
                  : data.customerName}
              </Text>

              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Text
                  type="caption1"
                  style={{
                    color: getPartnerReservationStatusColor(
                      data.status,
                      data.isBlock
                    ),
                  }}
                >
                  {getPartnerReservationStatusLabel(data.status, data.isBlock)}
                </Text>

                <Text type="caption1">|</Text>

                {data.customPickupLocation ? (
                  <>
                    <Icon
                      type="naked"
                      icon={<Home />}
                      fillColor={theme.colors.icon.color}
                      width={14}
                      height={14}
                    />

                    <Text type="caption1">Delivery</Text>
                  </>
                ) : (
                  <Text type="caption1">Pickup & Return</Text>
                )}

                <Text type="caption1">|</Text>

                {data.subscription ? (
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.chip.brand.color }}
                  >
                    Subscription
                  </Text>
                ) : (
                  <Text type="caption1">Daily Rental</Text>
                )}
              </View>
            </View>

            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
                padding: 16,
                gap: 16,
              }}
            >
              <View style={{ gap: 4 }}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Car
                </Text>

                <Pressable
                  onPress={() => {
                    router.push(`/partner/cars/${data.car.id}`);
                  }}
                >
                  <Text type="bodyBold">
                    {`${data.car.vehicleYear} ${data.car.brandName} ${data.car.modelName} (${data.car.licensePlate})`}
                  </Text>
                </Pressable>
              </View>

              {['confirmed', 'rental'].includes(data.status) ? (
                <View style={{ gap: 4 }}>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Guest number
                  </Text>

                  <Text type="bodyBold">{data.customerPhone}</Text>
                </View>
              ) : null}
            </View>

            <View style={styles.locationContainer}>
              <View
                style={{
                  padding: 8,
                  gap: 8,
                  flexDirection: 'row',
                }}
              >
                <View>
                  <Icon
                    type="naked"
                    icon={<Location />}
                    fillColor={theme.colors.icon.color}
                    width={16}
                    height={16}
                    style={{
                      width: 40,
                      height: 40,
                    }}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <Text
                    tx={
                      data.customPickupLocation && data.customReturnLocation
                        ? 'carId.deliveryLocation'
                        : 'carId.pickupLocation'
                    }
                    type="bodyRegular"
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {format(
                      toZonedTime(data.startTime, data.pickupLocation.timezone),
                      'MMM dd yyyy - hh:mm a'
                    )}
                  </Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {data.customPickupLocation || data.pickupLocation.address}
                  </Text>
                </View>
              </View>

              <View
                style={{
                  padding: 8,
                  gap: 8,
                  flexDirection: 'row',
                }}
              >
                <View>
                  <Icon
                    type="naked"
                    icon={<Location />}
                    fillColor={theme.colors.icon.color}
                    width={16}
                    height={16}
                    style={{
                      width: 40,
                      height: 40,
                    }}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <Text tx="carId.returnLocation" type="bodyRegular" />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {format(
                      toZonedTime(data.endTime, data.returnLocation.timezone),
                      'MMM dd yyyy - hh:mm a'
                    )}
                  </Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {data.customReturnLocation || data.returnLocation.address}
                  </Text>
                </View>
              </View>
            </View>

            <View style={{ padding: 16 }}>
              <Button
                text="Modify Trip"
                type="primarySmall"
                onPress={() => {
                  setShowModifyRentalModal(true);
                }}
                style={styles.button}
              />
            </View>
          </Card>

          <View style={styles.mainLayout}>
            {/* Left column */}
            <View style={styles.leftColumn}>
              <View style={{ gap: 16 }}>
                <Text type="title2">Trip Information</Text>

                <View>
                  <Input
                    label={'Starting miles'}
                    value={startingMiles}
                    keyboardType="number-pad"
                    onChangeText={(val) => {
                      setStartingMiles(val);
                    }}
                  />
                </View>

                <View>
                  <Input
                    label={'Ending miles'}
                    value={endingMiles}
                    keyboardType="number-pad"
                    onChangeText={(val) => {
                      setEndingMiles(val);
                    }}
                  />
                </View>

                <InputMulti
                  label="Notes"
                  text={notes || ''}
                  onChangeText={(s: string) => setNotes(s)}
                  placeholder="Enter notes about this reservation"
                  style={{ minHeight: 100 }}
                />

                <Button
                  text="Update"
                  type="primarySmall"
                  onPress={handleUpdateTrip}
                  style={{
                    width: 100,
                  }}
                  disabled={
                    (data.startingMileage?.toString() === startingMiles &&
                      data.endingMileage?.toString() === endingMiles &&
                      notes === data.hostNotes) ||
                    endingMiles < startingMiles
                  }
                />
              </View>

              <View>
                <Accordion
                  title="Photos"
                  children={
                    <View style={{ gap: 16 }}>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Upload up to 40 photos of your vehicle to verify its
                        condition. Pre-trip photos are required to ensure proper
                        damage coverage for your rental
                      </Text>

                      <Button
                        text="Upload"
                        type="primarySmall"
                        onPress={handleUploadPhotos}
                        style={styles.button}
                        disabled={!allowUploadingCarImages}
                      />

                      <ScrollView
                        contentContainerStyle={{
                          gap: 16,
                          flexGrow: 1,
                        }}
                        showsVerticalScrollIndicator={false}
                        nestedScrollEnabled
                      >
                        {images?.map((image, index) => {
                          const imageFileName = image.fileName || image.name;
                          let imageUrl = image.uri;

                          return (
                            <PartnerFileItem
                              key={`${imageFileName}-${index}`}
                              imageUrl={imageUrl}
                              uploadedBy={image.uploadedBy}
                              onDelete={() => {
                                setImages(images.filter((_, i) => i !== index));
                              }}
                              timestamp={image.createdAt}
                            />
                          );
                        })}
                      </ScrollView>

                      {allowUploadingCarImages &&
                      (data.carImages.length > 0 || images?.length > 0) ? (
                        <Button
                          text="Save"
                          type="primarySmall"
                          onPress={handleSavePhotos}
                          style={{ width: 75 }}
                          disabled={
                            images?.length === 0 && imgsToShow.length === 0
                          }
                        />
                      ) : (
                        <View>
                          <Text type="bodyRegular">
                            Photo submission is currently disabled. You can only
                            modify photos 24 hours before a trip begins or 24
                            hours after the trip ends.
                          </Text>
                        </View>
                      )}
                    </View>
                  }
                />
              </View>

              <View>
                <Accordion
                  title="Tolls"
                  children={
                    <View style={{ gap: 16 }}>
                      {isDesktop() ? (
                        <>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Upload up to 40 photos of the car to confirm its
                            condition
                          </Text>

                          <Button
                            text="Add Toll"
                            type="primarySmall"
                            onPress={() => {
                              setShowAddTollModal(true);
                            }}
                            style={styles.button}
                            disabled={!allowUploadingTolls}
                          />

                          {!allowUploadingTolls ? (
                            <Text type="bodyRegular">
                              You can only modify tolls for{' '}
                              {tollOffsetCutoffTimeInDays} days after the end of
                              the trip
                            </Text>
                          ) : null}

                          <DataTable
                            columns={[
                              {
                                key: 'toll-plaza-name',
                                title: 'TOLL PLAZA NAME',
                                style: { minWidth: 180 },
                              },
                              {
                                key: 'time',
                                title: 'TIME',
                                style: { minWidth: 150 },
                              },
                              {
                                key: 'amount',
                                title: 'AMOUNT',
                                style: { minWidth: 110 },
                              },
                              ...(isResla
                                ? [
                                    {
                                      key: 'actions',
                                      title: 'ACTIONS',
                                      numeric: true,
                                      style: { minWidth: 110 },
                                    },
                                  ]
                                : []),
                            ]}
                            rows={tollRows}
                            footerValues={tollFooterValues}
                          />

                          <View style={{ flexDirection: 'row', gap: 8 }}>
                            {data.tollImages.map((tollImage, index) => (
                              <View
                                key={`toll-image-${index}`}
                                style={{ position: 'relative' }}
                              >
                                <FileThumbnail
                                  url={tollImage}
                                  onImagePress={() => {
                                    setShowGallery(true);
                                    setGalleryPhotos(data.tollImages);
                                    setActivePhotoIdx(index);
                                  }}
                                />

                                {isResla ? (
                                  deletingTollImageUrl === tollImage ? (
                                    <View style={styles.tollImageDeleteButton}>
                                      <ActivityIndicator
                                        size="small"
                                        color={theme.colors.icon.color}
                                      />
                                    </View>
                                  ) : (
                                    <Pressable
                                      onPress={() =>
                                        handleDeleteTollImage(tollImage)
                                      }
                                      disabled={deletingTollImageUrl !== null}
                                      style={({ pressed }) => [
                                        styles.tollImageDeleteButton,
                                        {
                                          opacity:
                                            pressed ||
                                            deletingTollImageUrl !== null
                                              ? 0.5
                                              : 1,
                                        },
                                      ]}
                                    >
                                      <Icon
                                        type="naked"
                                        icon={<Cancel />}
                                        fillColor={theme.colors.icon.color}
                                        width={8}
                                        height={8}
                                      />
                                    </Pressable>
                                  )
                                ) : null}
                              </View>
                            ))}
                          </View>
                        </>
                      ) : (
                        <>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            To add or edit your reservation, visit on desktop.
                          </Text>

                          <ScrollView
                            contentContainerStyle={{
                              gap: 16,
                              flexGrow: 1,
                            }}
                            showsVerticalScrollIndicator={false}
                            nestedScrollEnabled
                          >
                            {data?.tolls.map((toll: Toll) => (
                              <TollCard
                                toll={toll}
                                key={`toll-${toll.id}`}
                                showAdminFee={false}
                              />
                            ))}
                          </ScrollView>
                        </>
                      )}
                    </View>
                  }
                />
              </View>

              <View>
                <Accordion
                  title="Charging Records"
                  children={
                    <View style={{ gap: 16 }}>
                      {isDesktop() ? (
                        <>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            You can only modify charging records for 72 hours
                            after the end of the trip
                          </Text>

                          <Button
                            text="Add Charging Record"
                            type="primarySmall"
                            onPress={() => {
                              setShowAddChargingRecordModal(true);
                            }}
                            style={styles.button}
                            disabled={!allowUploadingChargingRecords}
                          />

                          <DataTable
                            columns={[
                              {
                                key: 'charging-plaza-name',
                                title: 'CHARGING PLAZA NAME',
                                style: { minWidth: 200 },
                              },
                              {
                                key: 'time',
                                title: 'TIME',
                                style: { minWidth: 150 },
                              },
                              {
                                key: 'amount',
                                title: 'AMOUNT',
                                style: { minWidth: 110 },
                              },
                            ]}
                            rows={chargingRecordRows}
                            footerValues={chargingRecordFooterValues}
                          />

                          <View style={{ flexDirection: 'row', gap: 8 }}>
                            {data.chargingRecordImages.map(
                              (chargingRecordImage, index) => (
                                <FileThumbnail
                                  key={`charging-record-image-${index}`}
                                  url={chargingRecordImage}
                                  onImagePress={() => {
                                    setShowGallery(true);
                                    setGalleryPhotos(data.chargingRecordImages);
                                    setActivePhotoIdx(index);
                                  }}
                                />
                              )
                            )}
                          </View>
                        </>
                      ) : (
                        <>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            To add or edit your reservation, visit on desktop.
                          </Text>

                          <ScrollView
                            contentContainerStyle={{
                              gap: 16,
                              flexGrow: 1,
                            }}
                            showsVerticalScrollIndicator={false}
                            nestedScrollEnabled
                          >
                            {data?.chargingRecords.map(
                              (chargingRecord: ChargingRecord) => (
                                <SuperchargingCard
                                  chargingRecord={chargingRecord}
                                  key={`chargingRecord-${chargingRecord.id}`}
                                />
                              )
                            )}
                          </ScrollView>
                        </>
                      )}
                    </View>
                  }
                />
              </View>

              <View>
                <Accordion
                  title="Incidentals"
                  children={
                    <View style={{ gap: 16 }}>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Edit incidentals up to 3 months after the rental ends
                      </Text>

                      <Button
                        text="Add Incidental"
                        type="primarySmall"
                        onPress={() => {
                          if (isDesktop()) {
                            setShowAddIncidentalModal(true);
                          } else {
                            router.push(
                              `/partner/reservations/${data.id}/add-incidental`
                            );
                          }
                        }}
                        style={styles.button}
                        disabled={!allowUploadingIncidental}
                      />

                      <Alert
                        type={AlertType.BRAND}
                        title="Important information"
                        body={
                          'Incidentals will be reviewed for validity.\n\nApproved incidentals will usually be sent in a separate payout from your regular earnings.\n\nParking/speeding incidentals can be submitted for review up to 90 days after the rental ends, however all other types of incidentals can only be submitted within 24 hours of the rental ending.'
                        }
                      />

                      {isDesktop() ? (
                        <DataTable
                          columns={[
                            {
                              key: 'id',
                              title: 'ID',
                              style: { minWidth: 80 },
                            },
                            {
                              key: 'name',
                              title: 'NAME',
                              style: { minWidth: 160 },
                            },
                            {
                              key: 'time',
                              title: 'TIME',
                              style: { minWidth: 150 },
                            },
                            {
                              key: 'amount',
                              title: 'AMOUNT',
                              style: { minWidth: 110 },
                            },
                            {
                              key: 'status',
                              title: 'STATUS',
                              style: { minWidth: 140 },
                            },
                            {
                              key: 'payment-status',
                              title: 'PAYMENT STATUS',
                              style: { minWidth: 150 },
                            },
                          ]}
                          rows={incidentalRecordRows}
                          footerValues={incidentalFooterValues}
                        />
                      ) : (
                        <ScrollView
                          contentContainerStyle={{
                            gap: 16,
                            flexGrow: 1,
                          }}
                          showsVerticalScrollIndicator={false}
                          nestedScrollEnabled
                        >
                          {data?.incidentals.map((incidental: Incidental) => (
                            <Card
                              key={`incidental-${incidental.id}`}
                              style={{ padding: 16, gap: 16 }}
                            >
                              <View>
                                <Text
                                  type="caption1"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  Status
                                </Text>
                                <Text
                                  type="bodyRegular"
                                  style={{
                                    color:
                                      incidental.status === 'approved' ||
                                      incidental.status === 'issued'
                                        ? theme.colors.chip.success.color
                                        : incidental.status === 'denied' ||
                                          incidental.status === 'rejected'
                                        ? theme.colors.chip.error.color
                                        : incidental.status === 'pending'
                                        ? theme.colors.chip.pending.color
                                        : theme.colors.chip.brand.color,
                                  }}
                                >
                                  {capitalizeFirstLetter(incidental.status)}
                                </Text>
                              </View>

                              <View>
                                <Text
                                  type="caption1"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  ID
                                </Text>
                                <Text type="bodyRegular">{incidental.id}</Text>
                              </View>

                              <View>
                                <Text
                                  type="caption1"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  Name
                                </Text>
                                <Text type="bodyRegular">
                                  {snakeToTitleCase(incidental.category)}
                                </Text>
                              </View>

                              <View>
                                <Text
                                  type="caption1"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  Amount
                                </Text>
                                <Text type="bodyRegular">
                                  ${incidental.amount}
                                </Text>
                              </View>

                              <View>
                                <Text
                                  type="caption1"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  Time
                                </Text>
                                <Text type="bodyRegular">
                                  {format(
                                    toZonedTime(
                                      incidental.offenseDate,
                                      data.returnLocation.timezone
                                    ),
                                    'MMM dd yyyy - hh:mm a'
                                  )}
                                </Text>
                              </View>

                              {incidental.isPaid || incidental.isPaidAt ? (
                                <View>
                                  <Text
                                    type="caption1"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    Payment Status
                                  </Text>
                                  <Text type="bodyRegular">
                                    {incidental.isPaidAt
                                      ? `Paid ${format(
                                          toZonedTime(
                                            incidental.isPaidAt,
                                            data.returnLocation.timezone
                                          ),
                                          'MMMM d, yyyy'
                                        )}`
                                      : 'Paid'}
                                  </Text>
                                </View>
                              ) : incidental.claimOwnerResponse ? (
                                <View>
                                  <Text
                                    type="caption1"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    Payment Status
                                  </Text>
                                  <Text type="bodyRegular">
                                    {incidental.claimOwnerResponse}
                                  </Text>
                                </View>
                              ) : null}
                            </Card>
                          ))}
                        </ScrollView>
                      )}
                    </View>
                  }
                />
              </View>
            </View>

            {/* Right column */}
            <View style={styles.rightColumn}>
              {/* Rental Breakdown */}
              <Card>
                <View style={styles.breakdownHeader}>
                  <Text type="title2">Rental breakdown</Text>

                  {isDesktop() && showCustomerPriceColumn ? (
                    <View style={styles.breakdownPriceHeaders}>
                      <Text
                        type="caption1"
                        numberOfLines={1}
                        style={styles.breakdownCustomerHeader}
                      >
                        Customer price
                      </Text>

                      <Text
                        type="caption1"
                        numberOfLines={1}
                        style={styles.breakdownEarningsHeader}
                      >
                        Your earnings
                      </Text>
                    </View>
                  ) : null}
                </View>

                <View style={{ padding: 16, gap: 8 }}>
                  {!isDesktop() && showCustomerPriceColumn ? (
                    <View style={styles.breakdownPriceHeaders}>
                      <Text
                        type="caption1"
                        numberOfLines={1}
                        style={styles.breakdownCustomerHeader}
                      >
                        Customer price
                      </Text>

                      <Text
                        type="caption1"
                        numberOfLines={1}
                        style={styles.breakdownEarningsHeader}
                      >
                        Your earnings
                      </Text>
                    </View>
                  ) : null}
                  {isRegularSubscription ? (
                    <View style={{ gap: 16 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text type="bodyRegular">Subscription Period</Text>
                        <Text type="bodyRegular">
                          {data.subscription.renewalPeriod
                            .split('_')
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(' ')}
                        </Text>
                      </View>

                      {data.weeklyDiscount > 0 ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <View
                            style={{
                              flexDirection: 'row',
                              gap: 4,
                              alignItems: 'center',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.chip.brand.color }}
                            >
                              Weekly Discount
                            </Text>
                            <Tooltip
                              text="Customers automatically get a discount for booking longer.
                  See the FAQ for more details."
                            />
                          </View>
                          <Text type="bodyRegular">
                            {`-$${formatNumber(data.weeklyDiscount)}`}
                          </Text>
                        </View>
                      ) : null}

                      {data.monthlyDiscount > 0 ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <View
                            style={{
                              flexDirection: 'row',
                              gap: 4,
                              alignItems: 'center',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.chip.brand.color }}
                            >
                              Monthly Discount
                            </Text>
                            <Tooltip
                              text="Customers automatically get a discount for booking longer.
                  See the FAQ for more details."
                            />
                          </View>
                          <Text type="bodyRegular">
                            {`-$${formatNumber(data.monthlyDiscount)}`}
                          </Text>
                        </View>
                      ) : null}

                      {['cleaning', 'completed', 'billable', 'late'].includes(
                        data.status
                      ) ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="bodyRegular">Mileage Tier</Text>
                          <Text type="bodyRegular">
                            {`${data.subscription.mileageTier} miles`}
                          </Text>
                        </View>
                      ) : null}

                      {['cleaning', 'completed', 'billable'].includes(
                        data.status
                      ) ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="bodyRegular">Tier Cost</Text>
                          <Text type="bodyRegular">
                            {`$${formatNumber(data.subscription.tierCost)}`}
                          </Text>
                        </View>
                      ) : null}

                      <View style={{ width: '100%', alignItems: 'flex-end' }}>
                        <Pressable
                          onPress={() => {
                            router.push(`/partner/cars/${data.car.id}`);
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ textDecorationLine: 'underline' }}
                          >
                            Price Chart
                          </Text>
                        </Pressable>
                      </View>
                    </View>
                  ) : null}

                  {!isRegularSubscription &&
                  data.reservationRates.length > 0 ? (
                    <View style={{ gap: 4 }}>
                      {isDesktop() || !showCustomerPriceColumn ? (
                        <Text type="bodyRegular">
                          {`${data.car.brandName} ${snakeToTitleCase(
                            data.car.modelName
                          )}`}
                        </Text>
                      ) : null}
                      {data.reservationRates.map((rate) =>
                        !isDesktop() && showCustomerPriceColumn ? (
                          <View
                            key={`rate-${rate.id}`}
                            style={styles.breakdownSimpleRow}
                          >
                            <View style={{ flex: 1, gap: 2 }}>
                              <Text type="bodyRegular">
                                {`${data.car.brandName} ${snakeToTitleCase(
                                  data.car.modelName
                                )}`}
                              </Text>

                              <Text
                                type="caption1"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {`$${formatNumber(rate.dailyRate)}/day x${
                                  rate.numDays
                                } days`}
                              </Text>

                              <Text
                                type="caption1"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {`You Keep ${Math.round(
                                  (1 - data.managementCommissionPercentage) *
                                    100
                                )}% of the daily rate`}
                              </Text>
                            </View>

                            <View style={styles.breakdownPriceCell}>
                              <Text
                                type="bodyRegular"
                                style={[
                                  styles.breakdownCustomerPrice,
                                  { color: theme.colors.text.secondary },
                                ]}
                              >
                                {`$${formatNumber(
                                  rate.dailyRate * rate.numDays
                                )}`}
                              </Text>

                              <Text
                                type="bodyRegular"
                                style={styles.breakdownEarningsPrice}
                              >
                                {`$${formatNumber(rate.ownerEarnings)}`}
                              </Text>
                            </View>
                          </View>
                        ) : (
                          <View
                            key={`rate-${rate.id}`}
                            style={styles.breakdownItemRow}
                          >
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.text.secondary,
                                width: 90,
                              }}
                            >
                              {`$${formatNumber(rate.dailyRate)}/day`}
                            </Text>
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.text.secondary,
                                flex: 1,
                                textAlign: 'center',
                              }}
                            >
                              {`x${rate.numDays} days`}
                            </Text>
                            <View style={styles.breakdownPriceCell}>
                              {isEVFMC ? (
                                <Text
                                  type="bodyRegular"
                                  style={{
                                    color: theme.colors.text.secondary,
                                    width: 90,
                                    textAlign: 'right',
                                  }}
                                >
                                  {`$${formatNumber(
                                    rate.dailyRate * rate.numDays
                                  )}`}
                                </Text>
                              ) : null}
                              <Text
                                type="bodyRegular"
                                style={{ width: 90, textAlign: 'right' }}
                              >
                                {`$${formatNumber(rate.ownerEarnings)}`}
                              </Text>
                            </View>
                          </View>
                        )
                      )}
                      {isDesktop() || !showCustomerPriceColumn ? (
                        <Text
                          type="caption1"
                          style={{
                            color: theme.colors.text.secondary,
                            fontStyle: 'italic',
                          }}
                        >
                          {`You Keep ${Math.round(
                            (1 - data.managementCommissionPercentage) * 100
                          )}% of the daily rate`}
                        </Text>
                      ) : null}
                      {!data.subscription
                        ? data.reservationRates
                            .filter((rate) => rate.discountType)
                            .map((rate) => (
                              <View
                                key={`rate-discount-${rate.id}`}
                                style={styles.breakdownSimpleRow}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{
                                    color: theme.colors.chip.brand.color,
                                    flex: 1,
                                  }}
                                >
                                  {rate.discountType}
                                </Text>
                                <View style={styles.breakdownPriceCell}>
                                  {isEVFMC ? (
                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.chip.brand.color,
                                        width: 90,
                                        textAlign: 'right',
                                      }}
                                    >
                                      {`-$${formatNumber(rate.discountAmount)}`}
                                    </Text>
                                  ) : null}
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.chip.brand.color,
                                      width: 90,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {`-$${formatNumber(
                                      rate.ownerDiscountAmount
                                    )}`}
                                  </Text>
                                </View>
                              </View>
                            ))
                        : null}

                      {data.weeklyDiscount > 0 &&
                      isUnlimitedSubscription &&
                      isEVFMC ? (
                        <View style={styles.breakdownSimpleRow}>
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.chip.brand.color,
                              flex: 1,
                            }}
                          >
                            Weekly Discount
                          </Text>
                          <View style={styles.breakdownPriceCell}>
                            {isEVFMC ? (
                              <Text
                                type="bodyRegular"
                                style={{
                                  color: theme.colors.chip.brand.color,
                                  width: 90,
                                  textAlign: 'right',
                                }}
                              >
                                {`-$${formatNumber(data.weeklyDiscount)}`}
                              </Text>
                            ) : null}
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.chip.brand.color,
                                width: 90,
                                textAlign: 'right',
                              }}
                            >
                              {data.subscription
                                ? '-'
                                : `-$${formatNumber(data.ownerWeeklyDiscount)}`}
                            </Text>
                          </View>
                        </View>
                      ) : null}

                      {data.monthlyDiscount > 0 &&
                      isUnlimitedSubscription &&
                      isEVFMC ? (
                        <View style={styles.breakdownSimpleRow}>
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.chip.brand.color,
                              flex: 1,
                            }}
                          >
                            Monthly Discount
                          </Text>
                          <View style={styles.breakdownPriceCell}>
                            {isEVFMC ? (
                              <Text
                                type="bodyRegular"
                                style={{
                                  color: theme.colors.chip.brand.color,
                                  width: 90,
                                  textAlign: 'right',
                                }}
                              >
                                {`-$${formatNumber(data.monthlyDiscount)}`}
                              </Text>
                            ) : null}
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.chip.brand.color,
                                width: 90,
                                textAlign: 'right',
                              }}
                            >
                              {`-$${formatNumber(data.ownerMonthlyDiscount)}`}
                            </Text>
                          </View>
                        </View>
                      ) : null}
                    </View>
                  ) : null}

                  {isEVFMC ? (
                    !isDesktop() && showCustomerPriceColumn ? (
                      <View style={styles.breakdownSimpleRow}>
                        <View style={{ flex: 1, gap: 2 }}>
                          <Text type="bodyRegular">
                            {data.insurance.category}
                          </Text>

                          <Text
                            type="caption1"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {`$${formatNumber(
                              data.insuranceCost / billedDays
                            )}/day x${billedDays} days`}
                          </Text>
                        </View>

                        <View style={styles.breakdownPriceCell}>
                          <Text
                            type="bodyRegular"
                            style={[
                              styles.breakdownCustomerPrice,
                              { color: theme.colors.text.secondary },
                            ]}
                          >
                            {`$${formatNumber(data.insuranceCost)}`}
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={[
                              styles.breakdownEarningsPrice,
                              { color: theme.colors.text.secondary },
                            ]}
                          >
                            –
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={{ gap: 4 }}>
                        <Text type="bodyRegular">
                          {data.insurance.category}
                        </Text>
                        <View style={styles.breakdownItemRow}>
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                            }}
                          >
                            {`$${formatNumber(
                              data.insuranceCost / billedDays
                            )}/day`}
                          </Text>
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              flex: 1,
                              textAlign: 'center',
                            }}
                          >
                            {`x${billedDays} days`}
                          </Text>
                          <View style={styles.breakdownPriceCell}>
                            {showCustomerPriceColumn ? (
                              <Text
                                type="bodyRegular"
                                style={{
                                  color: theme.colors.text.secondary,
                                  width: 90,
                                  textAlign: 'right',
                                }}
                              >
                                {`$${formatNumber(data.insuranceCost)}`}
                              </Text>
                            ) : null}
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.text.secondary,
                                width: 90,
                                textAlign: 'right',
                              }}
                            >
                              -
                            </Text>
                          </View>
                        </View>
                      </View>
                    )
                  ) : null}

                  {data.extras
                    .filter(
                      (extra) =>
                        extra.title !== 'Custom Delivery' &&
                        (isEVFMC || extra.payoutAmount != null)
                    )
                    .map((extra) => {
                      const customerAmount = extra.recurring
                        ? extra.amount * data.numDays
                        : extra.amount;
                      return (
                        <View
                          key={`extra-${extra.id}`}
                          style={styles.breakdownSimpleRow}
                        >
                          <Text type="bodyRegular" style={{ flex: 1 }}>
                            {extra.title}
                          </Text>
                          <View style={styles.breakdownPriceCell}>
                            {showCustomerPriceColumn ? (
                              <Text
                                type="bodyRegular"
                                style={{
                                  color: theme.colors.text.secondary,
                                  width: 90,
                                  textAlign: 'right',
                                }}
                              >
                                {`$${formatNumber(customerAmount)}`}
                              </Text>
                            ) : null}
                            <Text
                              type="bodyRegular"
                              style={{ width: 90, textAlign: 'right' }}
                            >
                              {extra.payoutAmount != null
                                ? `$${formatNumber(extra.payoutAmount)}`
                                : ''}
                            </Text>
                          </View>
                        </View>
                      );
                    })}

                  {data.totalDeliveryCost > 0 ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text type="bodyRegular" style={{ flex: 1 }}>
                        Delivery fee
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`$${formatNumber(data.totalDeliveryCost)}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyRegular"
                          style={{ width: 90, textAlign: 'right' }}
                        >
                          {`$${formatNumber(data.ownerDeliveryPayout)}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}

                  {data.extraMiles > 0 && !data.subscription ? (
                    <View style={{ gap: 4 }}>
                      <Text type="bodyRegular">Additional Miles</Text>
                      <View style={styles.breakdownItemRow}>
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.text.secondary,
                            width: 80,
                          }}
                        >
                          {`$${formatNumber(
                            data.ownerAdditionalMilesCost / data.extraMiles
                          )}/mile`}
                        </Text>
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.text.secondary,
                            flex: 1,
                            textAlign: 'center',
                          }}
                        >
                          {`x${data.extraMiles} miles`}
                        </Text>
                        <View style={styles.breakdownPriceCell}>
                          {isEVFMC ? (
                            <Text
                              type="bodyRegular"
                              style={{
                                color: theme.colors.text.secondary,
                                width: 90,
                                textAlign: 'right',
                              }}
                            >
                              {`$${formatNumber(data.additionalMilesCost)}`}
                            </Text>
                          ) : null}
                          <Text
                            type="bodyRegular"
                            style={{ width: 90, textAlign: 'right' }}
                          >
                            {`$${formatNumber(data.ownerAdditionalMilesCost)}`}
                          </Text>
                        </View>
                      </View>
                    </View>
                  ) : null}

                  {data.manualDiscount > 0 &&
                  isEVFMC &&
                  !isRegularSubscription ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.chip.brand.color,
                          flex: 1,
                        }}
                      >
                        Manual Discount
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.chip.brand.color,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          {`-$${formatNumber(data.manualDiscount)}`}
                        </Text>
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.chip.brand.color,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          -
                        </Text>
                      </View>
                    </View>
                  ) : null}

                  {data.ownerDiscount > 0 ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.chip.brand.color,
                          flex: 1,
                        }}
                      >
                        Owner Discount
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.chip.brand.color,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`-$${formatNumber(
                              data.ownerDiscountEarningsImpact
                            )}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.chip.brand.color,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          {`-$${formatNumber(
                            data.ownerDiscountEarningsImpact
                          )}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}

                  <View style={styles.breakdownSimpleRow}>
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary, flex: 1 }}
                    >
                      Total Trip Days
                    </Text>
                    <Text type="bodyRegular">{data.numDays}</Text>
                  </View>
                </View>

                <View style={styles.breakdownTotals}>
                  {resolvedCustomerChargingAmount > 0 ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text type="bodyRegular" style={{ flex: 1 }}>
                        Charging
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`$${formatNumber(resolvedCustomerChargingAmount)}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.text.secondary,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          {data.passThroughCharging
                            ? '-'
                            : `$${formatNumber(data.resolvedChargingAmount)}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                  {data.resolvedTollsAmount > 0 ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text type="bodyRegular" style={{ flex: 1 }}>
                        Tolls
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`$${formatNumber(data.tollsUsedWithUpcharge)}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.text.secondary,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          {data.passThroughTolls
                            ? '-'
                            : `$${formatNumber(data.ownerTollsAmount)}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                  {billedIncidentals.map((incidental) => (
                    <View
                      key={`incidental-${incidental.id}`}
                      style={styles.breakdownSimpleRow}
                    >
                      <Text type="bodyRegular" style={{ flex: 1 }}>
                        {snakeToTitleCase(incidental.category)}
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`$${formatNumber(incidental.amountWithUpcharge)}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyRegular"
                          style={{
                            color: theme.colors.text.secondary,
                            width: 90,
                            textAlign: 'right',
                          }}
                        >
                          {`$${formatNumber(incidental.amount)}`}
                        </Text>
                      </View>
                    </View>
                  ))}
                  {showTotalEarnings &&
                    (resolvedCustomerChargingAmount > 0 ||
                      data.resolvedTollsAmount > 0 ||
                      billedIncidentals.length > 0) && (
                      <View
                        style={{
                          height: 1,
                          backgroundColor:
                            theme.colors.card.default.borderColor,
                          marginHorizontal: -16,
                        }}
                      />
                    )}
                  {showTotalEarnings ? (
                    <View style={styles.breakdownSimpleRow}>
                      <Text type="bodyBold" style={{ flex: 1 }}>
                        Total Earnings
                      </Text>
                      <View style={styles.breakdownPriceCell}>
                        {showCustomerPriceColumn ? (
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`$${formatNumber(data.totalCost)}`}
                          </Text>
                        ) : null}
                        <Text
                          type="bodyBold"
                          style={{ width: 90, textAlign: 'right' }}
                        >
                          {`$${formatNumber(data.ownerEarnings)}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                  {(data.resolvedChargingAmount > 0 ||
                    data.resolvedTollsAmount > 0 ||
                    data.incidentalsCost > 0) && (
                    <View
                      style={{
                        height: 1,
                        backgroundColor: theme.colors.card.default.borderColor,
                        marginHorizontal: -16,
                      }}
                    />
                  )}
                  {(data.reservationAdjustments ?? []).map(
                    (adjustment: ReservationAdjustment) => (
                      <View
                        key={adjustment.id}
                        style={styles.breakdownSimpleRow}
                      >
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.pending, flex: 1 }}
                        >
                          {adjustment.adjustmentType ===
                          'non_refundable_shortened_trip'
                            ? 'Shortened Trip'
                            : snakeToTitleCase(adjustment.adjustmentType)}
                        </Text>

                        <View style={styles.breakdownPriceCell}>
                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.pending,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {`${
                              adjustment.amount < 0 ? '-' : ''
                            }$${formatNumber(Math.abs(adjustment.amount))}`}
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                              width: 90,
                              textAlign: 'right',
                            }}
                          >
                            {'-'}
                          </Text>
                        </View>
                      </View>
                    )
                  )}
                </View>
              </Card>

              {/* Payment History */}
              {isEVFMC ? (
                <Card>
                  <View style={styles.breakdownHeader}>
                    <Text type="title2">Payment history</Text>
                  </View>
                  <View style={{ padding: 16, gap: 8 }}>
                    {(data.payments ?? []).length === 0 &&
                    (data.refunds ?? []).length === 0 ? (
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        No payment history yet
                      </Text>
                    ) : null}
                    {(data.payments ?? []).map((payment) => (
                      <View key={payment.id} style={styles.breakdownSimpleRow}>
                        <Text
                          type="bodyRegular"
                          style={[
                            { flex: 1 },
                            payment.isHold
                              ? { color: theme.colors.pending }
                              : null,
                          ]}
                        >
                          {payment.memo}
                        </Text>
                        <Text
                          type="bodyRegular"
                          style={
                            payment.isHold
                              ? { color: theme.colors.pending }
                              : {}
                          }
                        >
                          {`$${formatNumber(payment.amount)}`}
                        </Text>
                      </View>
                    ))}
                    {(data.refunds ?? []).map((refund, idx) => (
                      <View
                        key={`refund-${idx}`}
                        style={styles.breakdownSimpleRow}
                      >
                        <Text type="bodyRegular" style={{ flex: 1 }}>
                          {refund.memo}
                        </Text>
                        <Text type="bodyRegular">{`-$${formatNumber(
                          refund.amount
                        )}`}</Text>
                      </View>
                    ))}
                  </View>
                </Card>
              ) : null}

              {/* Outstanding Balance */}
              {isEVFMC ? (
                <Card>
                  <View style={styles.outstandingBalanceRow}>
                    <Text type="title2">Outstanding balance</Text>
                    <Text
                      type="bodyRegular"
                      style={
                        data.outstandingBalance > 0
                          ? { color: theme.colors.error }
                          : {}
                      }
                    >
                      {data.outstandingBalance > 0
                        ? `-$${formatNumber(data.outstandingBalance)}`
                        : `$${formatNumber(data.outstandingBalance)}`}
                    </Text>
                  </View>
                </Card>
              ) : null}
            </View>
          </View>
        </View>
      </KeyboardAwareScrollView>

      <ModifyRentalModal
        visible={showModifyRentalModal}
        onClose={() => {
          setShowModifyRentalModal(false);
        }}
        id={data.id}
      />

      <AddTollModal
        visible={showAddTollModal}
        onClose={() => setShowAddTollModal(false)}
        onSuccess={() => {
          setShowAddTollModal(false);
          refetch();
        }}
        reservation={data}
      />

      <AddChargingRecordModal
        visible={showAddChargingRecordModal}
        onClose={() => setShowAddChargingRecordModal(false)}
        onSuccess={() => {
          setShowAddChargingRecordModal(false);
          refetch();
        }}
        id={data.id}
        pickupTz={data.pickupLocation?.timezone || 'UTC'}
      />

      <AddIncidentalModal
        visible={showAddIncidentalModal}
        onClose={() => setShowAddIncidentalModal(false)}
        onSuccess={() => {
          setShowAddIncidentalModal(false);
          refetch();
        }}
        reservation={data}
      />

      <SetupDigitalKeyModal
        visible={showSetupDigitalKeyModal}
        onClose={() => setShowSetupDigitalKeyModal(false)}
        car={data.car}
        onVerifyKeySuccess={() => {
          refetch();
        }}
      />

      <KeyLimitModal
        visible={showKeyLimitModal}
        onClose={() => setShowKeyLimitModal(false)}
      />

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={1000}
        isVisible={showGallery}
        hasBackdrop
        onBackdropPress={() => {
          setShowGallery(false);
        }}
      >
        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <View
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              height: 600,
              width: 800,
              backgroundColor: theme.colors.background_2,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <View
              style={{
                alignSelf: 'center',
                marginTop: 16,
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <Text type="title2">
                {galleryPhotos.length > 0
                  ? galleryPhotos[activePhotoIdx].split('/').pop()
                  : ''}
              </Text>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setShowGallery(false);
                }}
                style={{
                  position: 'absolute',
                  right: 0,
                }}
              />
            </View>

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Pressable
                onPress={() => {
                  carouselRef.current?.prev();
                }}
              >
                <Icon
                  type="dressed"
                  icon={<ArrowLeft />}
                  fillColor={theme.colors.icon.color}
                />
              </Pressable>

              <View>
                <Carousel
                  ref={carouselRef}
                  width={400}
                  height={600 * 0.85}
                  data={galleryPhotos}
                  onProgressChange={progress}
                  onSnapToItem={(index) => {
                    setActivePhotoIdx(index);
                  }}
                  loop={false}
                  defaultIndex={activePhotoIdx}
                  snapEnabled
                  mode="parallax"
                  modeConfig={{
                    parallaxScrollingScale: 0.95,
                    parallaxScrollingOffset: 10,
                  }}
                  renderItem={({ item, index }) => {
                    return (
                      <View
                        key={`gallery-photo-${index}`}
                        style={{
                          flexGrow: 1,
                        }}
                      >
                        <Image
                          style={{ width: '100%', height: '100%' }}
                          source={item}
                          contentFit="contain"
                        />
                      </View>
                    );
                  }}
                />

                <Pagination.Basic
                  progress={progress}
                  data={galleryPhotos}
                  dotStyle={{
                    backgroundColor: theme.colors.paginationDots.empty,
                    borderRadius: 50,
                  }}
                  activeDotStyle={{
                    backgroundColor: theme.colors.paginationDots.filled,
                  }}
                  size={8}
                  containerStyle={{ gap: 10, marginTop: 20 }}
                  onPress={onPressPagination}
                />
              </View>

              <Pressable
                onPress={() => {
                  carouselRef.current?.next();
                }}
              >
                <Icon
                  type="dressed"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.icon.color}
                />
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollView: {
    flex: 1,
    borderRadius: 16,
  },
  scrollViewContent: {
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 0,
      md: 56,
    },
    flexGrow: 1,
    paddingBottom: 100,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  locationContainer: {
    borderBottomColor: theme.colors.card.default.borderColor,
    borderBottomWidth: 1,
    padding: 16,
    gap: 16,
    flexDirection: {
      md: 'row',
    },
  },
  button: {
    width: {
      md: 200,
    },
  },
  mainLayout: {
    gap: 32,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      md: 'flex-start',
    },
  },
  leftColumn: {
    flex: 1,
    gap: 32,
  },
  rightColumn: {
    gap: 16,
    width: {
      md: 649,
    },
  },
  breakdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  breakdownPriceHeaders: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  breakdownCustomerHeader: {
    width: {
      xs: 96,
      md: 110,
    },
    textAlign: 'right',
    color: theme.colors.text.secondary,
  },
  breakdownEarningsHeader: {
    width: {
      xs: 88,
      md: 80,
    },
    textAlign: 'right',
    color: theme.colors.text.secondary,
  },
  breakdownCustomerPrice: {
    width: {
      xs: 96,
      md: 90,
    },
    textAlign: 'right',
  },
  breakdownEarningsPrice: {
    width: {
      xs: 88,
      md: 90,
    },
    textAlign: 'right',
  },
  breakdownItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breakdownSimpleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  breakdownPriceCell: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  breakdownTotals: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
    padding: 16,
    gap: 8,
  },
  outstandingBalanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  tollImageDeleteButton: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: theme.colors.background_2,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
